import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Activity, MapPinned, Pencil, Sprout, Trees, UserRound } from "lucide-react";
import { httpServices as services } from "@/services";
import type { FarmAnnualYield, FarmDetail } from "@/types/domain";
import type { AgreementRequirementReference } from "@/services/contracts";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Detail = ({ label, value, testId }: { label: string; value?: string | number; testId: string }) =>
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 font-medium" data-testid={testId}>{value ?? "Not recorded"}</p></div>;

export default function FarmProfilePage() {
  const { farmId = "" } = useParams();
  const [farm, setFarm] = useState<FarmDetail>();
  const [annualYields, setAnnualYields] = useState<FarmAnnualYield[]>([]);
  const [agreementRequirements, setAgreementRequirements] = useState<AgreementRequirementReference[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([services.farms.get(farmId), services.farms.listFarmAnnualYields(farmId)])
      .then(async ([item, yields]) => {
        if (!item) return setError("Farm record was not found.");
        setFarm(item);
        setAnnualYields(yields);
        try { setAgreementRequirements(await services.farmers.listAgreementRequirements(item.farmerId)); }
        catch { setAgreementRequirements([]); }
      })
      .catch((e: Error) => setError(e.message || "Farm could not be loaded."));
  }, [farmId]);

  const derivedAcreage = useMemo(() => farm?.plotsData.reduce((sum, plot) => sum + (plot.reportedAreaAcres || 0), 0) || 0, [farm]);
  const verifiedBoundaries = useMemo(() => farm?.plotsData.filter((plot) => plot.boundaryVerified).length || 0, [farm]);
  const pendingPlotAgreementIds = useMemo(() => new Set(agreementRequirements.filter((item) => item.requirementType === "PLOT_ADDITION" && item.status === "PENDING" && item.plotId).map((item) => item.plotId as string)), [agreementRequirements]);
  const generalAgreementPending = useMemo(() => agreementRequirements.some((item) => item.requirementType === "GENERAL_ENROLMENT" && item.status === "PENDING"), [agreementRequirements]);
  const pendingAgreementCount = useMemo(() => agreementRequirements.filter((item) => item.status === "PENDING").length, [agreementRequirements]);

  if (error) return <ErrorState message={error} />;
  if (!farm) return <LoadingState label="Loading Farm and Plots" />;

  return <div className="space-y-6" data-testid="farm-profile-page">
    <PageHeader
      eyebrow="Farm registry"
      title={farm.name}
      description={`${farm.code} · ${farm.projectName}`}
      backTo="/farms"
      action={<Button variant="outline" asChild data-testid="farm-field-update-button"><Link to={`/farms/${farm.id}/edit`}><Pencil className="mr-2 size-4" />Manage Farm</Link></Button>}
    />

    <div className="flex flex-wrap gap-2">
      <StatusBadge status={farm.farmStatus} testId="farm-profile-status" />
      <StatusBadge status={farm.farmerRecordStatus || farm.recordStatus} testId="farm-profile-farmer-registration-status" />
      <Badge variant="outline">{farm.plotsData.length} Plot{farm.plotsData.length === 1 ? "" : "s"}</Badge>
      <Badge variant="outline">{verifiedBoundaries}/{farm.plotsData.length} Plot boundaries verified</Badge>
      {pendingAgreementCount > 0 && <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">{pendingAgreementCount} agreement requirement{pendingAgreementCount === 1 ? "" : "s"} outstanding</Badge>}
    </div>

    <Tabs defaultValue="overview" className="space-y-5" data-testid="farm-profile-tabs">
      <TabsList className="h-auto flex-wrap justify-start">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="plots">Plots &amp; crops</TabsTrigger>
        <TabsTrigger value="yield">Yield</TabsTrigger>
        <TabsTrigger value="activity">Activity</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><UserRound className="size-4" />Farmer</CardTitle></CardHeader>
          <CardContent>
            <Link to={`/farmers/${farm.farmerId}`} className="font-semibold text-primary hover:underline">{farm.farmerName}</Link>
            <p className="font-mono text-xs text-muted-foreground">{farm.farmerCode}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Farm summary</CardTitle><CardDescription>The Farm is the umbrella holding. Acreage and mapping are derived from its Plots.</CardDescription></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Detail label="Total Plot acreage" value={derivedAcreage ? `${derivedAcreage.toFixed(2)} ac` : "Pending Plot acreages"} testId="farm-derived-area" />
            <Detail label="Active Plots" value={farm.plotsData.length} testId="farm-plot-count" />
            <Detail label="Verified Plot boundaries" value={`${verifiedBoundaries}/${farm.plotsData.length}`} testId="farm-verified-plots" />
            <div><p className="text-xs text-muted-foreground">Land location</p><p className="mt-1 text-sm text-muted-foreground">Precise location, GPS centre and boundaries are recorded separately on each Plot.</p></div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="plots" className="space-y-4" data-testid="farm-plots-section">
        {farm.plotsData.length ? farm.plotsData.map((plot, index) => <Card key={plot.id}>
          <CardContent className="grid gap-4 p-5 lg:grid-cols-[1fr_180px_180px_auto]">
            <div>
              <div className="flex items-center gap-2"><Sprout className="size-4 text-primary" /><p className="font-semibold">Plot {index + 1}: {plot.name}</p></div>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{plot.code}</p>
              <p className="mt-2 text-sm text-muted-foreground">{plot.currentLandUse || "Current land use not recorded"}</p>
            </div>
            <Detail label="Acreage" value={plot.reportedAreaAcres != null ? `${plot.reportedAreaAcres} ac` : "Pending"} testId={`plot-area-${plot.id}`} />
            <Detail label="Ownership / tenure" value={plot.ownershipType || "Pending"} testId={`plot-ownership-${plot.id}`} />
            <div className="flex flex-col gap-2">
              <StatusBadge status={plot.status} testId={`plot-status-${plot.id}`} />
              <Badge variant="outline">{plot.boundaryVerified ? "GPS verified" : plot.boundaryCaptured ? "GPS review pending" : "GPS pending"}</Badge>
              {pendingPlotAgreementIds.has(plot.id) && <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">Signed Plot agreement outstanding</Badge>}
              {index === 0 && generalAgreementPending && <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">General enrolment agreement outstanding</Badge>}
              <Button size="sm" asChild><Link to={`/farms/${farm.id}/plots/${plot.id}/map`}><MapPinned className="mr-2 size-4" />Manage Plot</Link></Button>
            </div>
          </CardContent>
        </Card>) : <div className="rounded-lg border border-dashed p-5">
          <p className="text-sm text-muted-foreground">No Plots registered for this Farm yet.</p>
          <Button className="mt-3" asChild><Link to={`/farms/${farm.id}/edit`}>Add the first Plot</Link></Button>
        </div>}
      </TabsContent>

      <TabsContent value="yield">
        <Card>
          <CardHeader>
            <CardTitle>Farm yield summary</CardTitle>
            <CardDescription>Expected harvest is the sum of the Plot forecasts. Actual harvest is the verified produce recorded against this Farm, because produce from different Plots may be combined during harvesting and delivery.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {annualYields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No annual yield information yet. Add expected yield forecasts inside the individual Plots; verified Farm deliveries will populate actual harvest.</p>
            ) : annualYields.map((item) => (
              <div key={item.calendarYear} className="grid gap-3 rounded-lg border p-4 text-sm sm:grid-cols-5">
                <span className="font-semibold">{item.calendarYear}</span>
                <span>Expected: {item.expectedYieldKg == null ? "Pending Plot forecasts" : `${item.expectedYieldKg} kg`}</span>
                <span>Actual Farm harvest: {item.actualHarvestKg == null ? "No verified delivery yet" : `${item.actualHarvestKg} kg`}</span>
                <span>Variance: {item.varianceKg == null ? "—" : `${item.varianceKg} kg`}</span>
                <span>{item.variancePct == null ? "No comparison yet" : `${item.variancePct}% vs expected`}</span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">A variance is a management and agronomic signal. It does not assign an exact actual yield to individual Plots unless produce was genuinely traceable that way.</p>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="activity">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="size-4" />Farm history</CardTitle><CardDescription>Operational history will be populated from real audit events.</CardDescription></CardHeader>
          <CardContent><p className="flex items-center gap-2 text-sm text-muted-foreground"><Trees className="size-4" />Farm linked to {farm.farmerName}. Plot-level activity is managed from each Plot.</p></CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  </div>;
}
